import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, MapPin } from "lucide-react";
import { endpoints } from "@/lib/api";
import { loadGoogleMaps, RW_CENTER, pinSvg } from "@/lib/googleMaps";

const LEVELS = ["province", "district", "sector", "cell", "village"];
const sel = "h-11 w-full px-3 rounded-xl bg-[#0A0C10] border border-[#273042] text-white text-sm disabled:opacity-40";
const pin = L.divIcon({ className: "", html: '<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#E11D2A;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.5)"></div>', iconSize: [22, 22], iconAnchor: [11, 22] });

// Rwanda administrative cascade (province → village) from our API.
export const RwandaLocation = ({ value, onChange }) => {
  const [opts, setOpts] = useState({});
  useEffect(() => { endpoints.rwLocations({}).then((r) => setOpts((o) => ({ ...o, province: r.data.items }))); }, []);
  const pick = async (lvl, v) => {
    const i = LEVELS.indexOf(lvl);
    const next = { ...value, [lvl]: v };
    LEVELS.slice(i + 1).forEach((l) => { next[l] = ""; });
    onChange(next);
    if (v && i < 4) {
      const params = Object.fromEntries(LEVELS.slice(0, i + 1).map((l) => [l, next[l]]));
      const r = await endpoints.rwLocations(params);
      setOpts((o) => ({ ...o, [LEVELS[i + 1]]: r.data.items }));
    }
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="rw-location">
      {LEVELS.map((l, i) => (
        <select key={l} value={value[l] || ""} disabled={i > 0 && !value[LEVELS[i - 1]]} onChange={(e) => pick(l, e.target.value)} className={sel} data-testid={`rw-${l}`}>
          <option value="">{l.charAt(0).toUpperCase() + l.slice(1)}</option>
          {(opts[l] || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>))}
    </div>
  );
};

// Exact business location: draggable pin on OpenStreetMap; centres on the chosen village automatically.
export const MapPinPicker = ({ lat, lng, onChange, focus }) => {
  const box = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [msg, setMsg] = useState("");
  const [engine, setEngine] = useState("");
  useEffect(() => {
    if (map.current) return undefined;
    let dead = false;
    const out = (la, ln) => onChange(+la.toFixed(6), +ln.toFixed(6));
    loadGoogleMaps().then((g) => {
      if (dead || map.current) return;
      const start = lat && lng ? { lat, lng } : RW_CENTER;
      if (g) {
        const m = new g.Map(box.current, { center: start, zoom: lat ? 18 : 9, mapTypeControl: true, streetViewControl: false, fullscreenControl: true,
          mapTypeControlOptions: { mapTypeIds: ["roadmap", "hybrid"] }, gestureHandling: "greedy" });
        const place = (ll) => {
          if (!marker.current) {
            marker.current = new g.Marker({ position: ll, map: m, draggable: true, icon: pinSvg() });
            marker.current.addListener("dragend", (e) => out(e.latLng.lat(), e.latLng.lng()));
          } else marker.current.setPosition(ll);
          out(typeof ll.lat === "function" ? ll.lat() : ll.lat, typeof ll.lng === "function" ? ll.lng() : ll.lng);
        };
        m.addListener("click", (e) => place(e.latLng));
        map.current = { setView: (ll, z) => { m.setCenter(ll); m.setZoom(z); }, place,
          geocode: (q) => new g.Geocoder().geocode({ address: q, componentRestrictions: { country: "RW" } }, (res, st) => {
            if (st === "OK" && res[0]) { m.fitBounds(res[0].geometry.viewport); if (m.getZoom() > 17) m.setZoom(17); }
          }) };
        if (lat && lng) place(start);
        setEngine("google");
        return;
      }
      const m = L.map(box.current, { zoomControl: true }).setView([start.lat, start.lng], lat ? 17 : 9);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(m);
      const place = (ll) => {
        if (!marker.current) {
          marker.current = L.marker(ll, { draggable: true, icon: pin }).addTo(m);
          marker.current.on("dragend", () => { const q = marker.current.getLatLng(); out(q.lat, q.lng); });
        } else marker.current.setLatLng(ll);
        out(ll.lat, ll.lng);
      };
      m.on("click", (e) => place(e.latlng));
      map.current = { setView: (ll, z) => m.setView([ll.lat, ll.lng], z), place,
        geocode: (q) => fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=rw&q=${encodeURIComponent(q)}`)
          .then((r) => r.json()).then((j) => { if (j[0]) m.setView([+j[0].lat, +j[0].lon], 15); }).catch(() => {}) };
      if (lat && lng) place(L.latLng(lat, lng));
      setEngine("osm");
    });
    return () => { dead = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (focus && map.current) map.current.geocode(focus); }, [focus, engine]);
  const locate = () => {
    if (!navigator.geolocation) return setMsg("Location is not available on this device.");
    setMsg("Finding you…");
    navigator.geolocation.getCurrentPosition((p) => { const ll = { lat: p.coords.latitude, lng: p.coords.longitude }; map.current.setView(ll, 18); map.current.place(ll); setMsg(""); },
      () => setMsg("Couldn't get your location — tap the map instead."), { enableHighAccuracy: true, timeout: 15000 });
  };
  return (
    <div data-testid="map-pin-picker">
      <div ref={box} className="w-full h-72 rounded-xl overflow-hidden border border-[#273042] z-0" data-testid="map-canvas" data-engine={engine} />
      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
        <button type="button" onClick={locate} className="h-9 px-3 rounded-lg border border-[#273042] text-white inline-flex items-center gap-1.5" data-testid="map-locate-btn"><LocateFixed className="w-4 h-4" /> Use my current location</button>
        <span className="text-slate-400 flex items-center gap-1" data-testid="map-coords"><MapPin className="w-3.5 h-3.5 text-[#E11D2A]" />{lat ? `${lat}, ${lng}` : "Tap the map to drop the pin at the exact spot"}</span>
        {lat && <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer" className="text-[#E11D2A] font-semibold" data-testid="map-google-link">Check in Google Maps</a>}
        {msg && <span className="text-amber-300">{msg}</span>}
      </div>
    </div>
  );
};
